import { LayoutPanelLeft } from "lucide-react";
import { Text } from "@/shared/ui";
import { cn } from "@/shared/ui/utils/tailwind";
import type { AggregationResult } from "@services/housien/dashboards";
import {
  bucketLabel,
  dimensionLabel,
  unsupportedMessage,
} from "../data/dimension-labels";

/**
 * A single aligned bar in a dashboard panel chart: the value label on top, the
 * bar scaled against the plot height, and the (non-clipped) category label
 * below. Ported from the DS-compliant prototype `ChartBar`, which already
 * handles the mobile chart-overlap (a reserved top band so the tallest bar's
 * value label never collides with the caption).
 *
 * @param props.label The category label shown under the bar.
 * @param props.value The server-computed count (bar height + value label).
 * @param props.max The series maximum, used to scale the bar.
 * @param props.plotHeight The pixel height of the plot area.
 * @returns The bar column element.
 */
function ChartBar({
  label,
  value,
  max,
  plotHeight,
}: {
  label: string;
  value: number;
  max: number;
  plotHeight: number;
}) {
  const VALUE_HEADROOM = 22;
  const barMax = Math.max(plotHeight - VALUE_HEADROOM, 8);
  const height = Math.max(4, Math.round((value / max) * barMax));
  return (
    <div className="flex min-w-0 flex-1 flex-col items-center justify-end gap-(--space-1)">
      <Text variant="micro" tone="secondary" fw={700} component="span" className="leading-none">
        {value}
      </Text>
      <div
        className="w-full max-w-[72px] rounded-t-(--radius-sm) bg-(--seed-base)"
        style={{ height }}
        aria-label={`${label}: ${value}`}
      />
    </div>
  );
}

/**
 * Renders ONE dashboard panel from a REAL server-computed
 * {@link AggregationResult}. The bar heights and value labels are the backend
 * `aggregate?group_by=...` bucket counts verbatim — no arithmetic happens in
 * the client, so a rendered count always equals the backend bucket (AC-DASH-1).
 *
 * When the dimension is unsupported it renders the honest "não é possível
 * agrupar por X" message (AC-DASH-3) instead of an empty chart.
 *
 * @param props.result The server-computed aggregation for one dimension.
 * @returns The panel element.
 */
export function DashboardPanelView({ result }: { result: AggregationResult }) {
  const label = dimensionLabel(result.groupBy);

  if (!result.supported) {
    return (
      <PanelShell title={`Distribuição por ${label}`}>
        <Text variant="meta" tone="secondary" data-testid="dashboard-panel-unsupported">
          {unsupportedMessage(result.groupBy, result.allowed)}
        </Text>
      </PanelShell>
    );
  }

  if (result.buckets.length === 0) {
    return (
      <PanelShell title={`Distribuição por ${label}`}>
        <Text variant="meta" tone="secondary">
          Nenhum imóvel encontrado para distribuição por {label}.
        </Text>
      </PanelShell>
    );
  }

  const max = Math.max(...result.buckets.map((bucket) => bucket.count), 1);
  const plotHeight = 120;

  return (
    <PanelShell title={`Distribuição por ${label}`} total={result.total}>
      <div className="flex flex-col gap-(--space-2)">
        <div className="relative" style={{ height: plotHeight }}>
          <div className="absolute inset-0 flex flex-col justify-between" aria-hidden>
            {[0, 1, 2, 3].map((line) => (
              <div key={line} className="h-px w-full bg-(--border-ultra-subtle)" />
            ))}
          </div>
          <div className="absolute inset-0 flex items-end gap-(--space-2) sm:gap-(--space-6)">
            {result.buckets.map((bucket) => (
              <ChartBar
                key={bucket.key}
                label={bucketLabel(result.groupBy, bucket.key)}
                value={bucket.count}
                max={max}
                plotHeight={plotHeight}
              />
            ))}
          </div>
        </div>
        <div className="flex items-start gap-(--space-2) sm:gap-(--space-6)">
          {result.buckets.map((bucket) => (
            <Text
              key={bucket.key}
              variant="micro"
              tone="tertiary"
              component="span"
              className="min-w-0 flex-1 truncate text-center leading-tight"
            >
              {bucketLabel(result.groupBy, bucket.key)}
            </Text>
          ))}
        </div>
      </div>
    </PanelShell>
  );
}

/**
 * The raised, bordered surface wrapping one panel's chart (or message), with a
 * caption header and an optional total chip.
 *
 * @param props.title The panel caption.
 * @param props.total Optional total chip value.
 * @param props.children The panel body.
 * @returns The panel surface element.
 */
function PanelShell({
  title,
  total,
  children,
}: {
  title: string;
  total?: number;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex w-full flex-col gap-(--space-3) rounded-(--radius-md) border border-(--border-subtle)",
        "bg-(--surface-primary) p-(--space-4) shadow-[var(--shadow-sm)]",
      )}
    >
      <div className="flex items-center gap-(--space-2)">
        <Text
          component="span"
          className="flex size-7 shrink-0 items-center justify-center rounded-(--radius-sm) bg-(--selection-selected) text-(--seed-base)"
          aria-hidden
        >
          <LayoutPanelLeft width={15} height={15} />
        </Text>
        <Text variant="bodySm" fw={700} className="min-w-0 flex-1 truncate">
          {title}
        </Text>
        {typeof total === "number" && (
          <Text
            component="span"
            className="shrink-0 rounded-full border border-(--border-subtle) bg-(--surface-secondary) px-(--space-2-5) py-(--space-0-5)"
          >
            <Text variant="micro" tone="secondary" fw={600} component="span">
              {total} imóveis
            </Text>
          </Text>
        )}
      </div>
      {children}
    </div>
  );
}
